/**
 * usePhaseProcessor Hook
 *
 * Phase 6-1: processPhase 関数を Hook 化
 *
 * フェイズ処理ロジックを管理:
 * - case 0: ターン開始フェイズ（SP追加、状態異常処理、復活処理等）
 * - case 1: ドローフェイズ（1枚ドロー）
 * - case 2: メインフェイズ（プレイヤー操作待ち）
 * - case 3: バトルフェイズ（先攻1ターン目は攻撃不可）
 * - case 4: エンドフェイズ（状態異常処理、ターン終了効果）
 */

import { clearTemporaryAttack } from '../../engine/temporaryAttack';
import { useCallback } from 'react';
import type React from 'react';
import { MAX_SP } from '../../utils/constants';
import { STATUS_EFFECT_TYPES, statusEffectEngine, getStatusDisplayName } from '../../engine/statusEffects';
import type { StatusEffectTypeValue } from '../../engine/statusEffects';
import type { PlayerStatusEffect } from '../../engine/statusEffects/statusEngine';
import { fireTrigger, registerCardTriggers, resetTurnFlags, getCardGraveyardTriggers } from '../../engine/triggerEngine';
import { TRIGGER_TYPES } from '../../engine/triggerTypes';
import { processStatusEffectsTurnStart, processStatusEffectsEndPhase } from '../../engine/effectHelpers';
import { processLinkEndPhaseDamage } from '../../engine/keywordAbilities';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import { actions as gameActions } from '../../engine/gameEngine';
import type { MonsterInstance, HandCard, CardWithId, CardData, FieldCardInstance, PhaseCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type {
  PendingCageRelease,
  PendingHandSelection,
  PendingGraveyardSelection,
  PendingDeckReview,
  PendingMonsterTarget,
} from '../../types/ui';
import type { DeepSeaZone } from '../../engine/deepSeaZone/types';
import { incrementDepth, hasAnkokuSuiikiKeyword } from '../../engine/deepSeaZone/deepSeaManager';

interface UsePhaseProcessorDeps {
  // ゲーム状態（読み取り）
  currentPlayer: PlayerNumber;
  isFirstTurn: boolean;
  firstPlayer: PlayerNumber;
  turn: number;
  pendingOpponentSPRest: PlayerNumber | null;
  summoningSickness: boolean;
  secondPlayerBonusSP: boolean;

  // プレイヤー1 状態
  p1Life: number;
  p1Field: (MonsterInstance | null)[];
  p1Hand: HandCard[];
  p1Deck: CardWithId[];
  p1Graveyard: CardWithId[];
  p1ActiveSP: number;
  p1RestedSP: number;
  p1FieldCard: FieldCardInstance | CardData | null;
  p1PhaseCard: PhaseCardInstance | null;
  p1StatusEffects: PlayerStatusEffect[];
  p1NextTurnSPBonus: number;
  p1SpReduction: number;
  p1BonusSP: number;
  p1MagicBlocked: boolean;

  // プレイヤー2 状態
  p2Life: number;
  p2Field: (MonsterInstance | null)[];
  p2Hand: HandCard[];
  p2Deck: CardWithId[];
  p2Graveyard: CardWithId[];
  p2ActiveSP: number;
  p2RestedSP: number;
  p2FieldCard: FieldCardInstance | CardData | null;
  p2PhaseCard: PhaseCardInstance | null;
  p2StatusEffects: PlayerStatusEffect[];
  p2NextTurnSPBonus: number;
  p2SpReduction: number;
  p2BonusSP: number;
  p2MagicBlocked: boolean;

  // 深海領域
  p1DeepSeaZone: DeepSeaZone;
  p2DeepSeaZone: DeepSeaZone;

  // Setters - プレイヤー1
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP1FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>;
  setP1PhaseCard: React.Dispatch<React.SetStateAction<PhaseCardInstance | null>>;
  setP1StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setP1NextTurnSPBonus: React.Dispatch<React.SetStateAction<number>>;
  setP1SpReduction: React.Dispatch<React.SetStateAction<number>>;
  setP1BonusSP: React.Dispatch<React.SetStateAction<number>>;
  setP1MagicBlocked: React.Dispatch<React.SetStateAction<boolean>>;

  // Setters - プレイヤー2
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>;
  setP2PhaseCard: React.Dispatch<React.SetStateAction<PhaseCardInstance | null>>;
  setP2StatusEffects: React.Dispatch<React.SetStateAction<PlayerStatusEffect[]>>;
  setP2NextTurnSPBonus: React.Dispatch<React.SetStateAction<number>>;
  setP2SpReduction: React.Dispatch<React.SetStateAction<number>>;
  setP2BonusSP: React.Dispatch<React.SetStateAction<number>>;
  setP2MagicBlocked: React.Dispatch<React.SetStateAction<boolean>>;

  // Setters - 深海領域
  setP1DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;
  setP2DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;

  // UI Setters
  setChargeUsedThisTurn: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingCageRelease: React.Dispatch<React.SetStateAction<PendingCageRelease | null>>;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelection | null>>;
  setPendingGraveyardSelection: React.Dispatch<React.SetStateAction<PendingGraveyardSelection | null>>;
  setShowGraveyardViewer: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReview | null>>;
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;

  // Utilities
  addLog: (message: string, type?: 'info' | 'damage' | 'heal') => void;
  dispatch: (action: unknown) => void;
}

interface PlayerData {
  life: number;
  setLife: React.Dispatch<React.SetStateAction<number>>;
  deck: CardWithId[];
  setDeck: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  hand: HandCard[];
  setHand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  field: (MonsterInstance | null)[];
  setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  graveyard: CardWithId[];
  setGraveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>;
  activeSP: number;
  setActiveSP: React.Dispatch<React.SetStateAction<number>>;
  restedSP: number;
  setRestedSP: React.Dispatch<React.SetStateAction<number>>;
  fieldCard: FieldCardInstance | CardData | null;
  setFieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | CardData | null>>;
  phaseCard: PhaseCardInstance | null;
  setPhaseCard: React.Dispatch<React.SetStateAction<PhaseCardInstance | null>>;
  spBonus: number;
  setSpBonus: React.Dispatch<React.SetStateAction<number>>;
  spReduction: number;
  setSpReduction: React.Dispatch<React.SetStateAction<number>>;
  bonusSP: number;
  setBonusSP: React.Dispatch<React.SetStateAction<number>>;
}

interface UsePhaseProcessorReturn {
  processPhase: (phaseIndex: number) => void;
}

/**
 * @param deps - 依存関係オブジェクト
 * @returns { processPhase }
 */
export const usePhaseProcessor = (deps: UsePhaseProcessorDeps): UsePhaseProcessorReturn => {
  const {
    // ゲーム状態（読み取り）
    currentPlayer,
    isFirstTurn,
    firstPlayer,
    turn,
    pendingOpponentSPRest,
    summoningSickness,
    secondPlayerBonusSP,

    // プレイヤー1 状態
    p1Life,
    p1Field,
    p1Hand,
    p1Deck,
    p1Graveyard,
    p1ActiveSP,
    p1RestedSP,
    p1FieldCard,
    p1PhaseCard,
    p1StatusEffects,
    p1NextTurnSPBonus,
    p1SpReduction,
    p1BonusSP,
    p1MagicBlocked,

    // プレイヤー2 状態
    p2Life,
    p2Field,
    p2Hand,
    p2Deck,
    p2Graveyard,
    p2ActiveSP,
    p2RestedSP,
    p2FieldCard,
    p2PhaseCard,
    p2StatusEffects,
    p2NextTurnSPBonus,
    p2SpReduction,
    p2BonusSP,
    p2MagicBlocked,

    // 深海領域
    p1DeepSeaZone,
    p2DeepSeaZone,

    // Setters - プレイヤー1
    setP1Life,
    setP1Field,
    setP1Hand,
    setP1Deck,
    setP1Graveyard,
    setP1ActiveSP,
    setP1RestedSP,
    setP1FieldCard,
    setP1PhaseCard,
    setP1StatusEffects,
    setP1NextTurnSPBonus,
    setP1SpReduction,
    setP1BonusSP,
    setP1MagicBlocked,

    // Setters - プレイヤー2
    setP2Life,
    setP2Field,
    setP2Hand,
    setP2Deck,
    setP2Graveyard,
    setP2ActiveSP,
    setP2RestedSP,
    setP2FieldCard,
    setP2PhaseCard,
    setP2StatusEffects,
    setP2NextTurnSPBonus,
    setP2SpReduction,
    setP2BonusSP,
    setP2MagicBlocked,

    // Setters - 深海領域
    setP1DeepSeaZone,
    setP2DeepSeaZone,

    // UI Setters
    setChargeUsedThisTurn,
    setPendingCageRelease,
    setPendingHandSelection,
    setPendingGraveyardSelection,
    setShowGraveyardViewer,
    setPendingDeckReview,
    setPendingMonsterTarget,

    // Utilities
    addLog,
    dispatch,
  } = deps;

  // プレイヤーデータを取得するヘルパー
  const getCurrentPlayerData = useCallback((): PlayerData => {
    if (currentPlayer === 1) {
      return {
        life: p1Life, setLife: setP1Life,
        deck: p1Deck, setDeck: setP1Deck,
        hand: p1Hand, setHand: setP1Hand,
        field: p1Field, setField: setP1Field,
        graveyard: p1Graveyard, setGraveyard: setP1Graveyard,
        activeSP: p1ActiveSP, setActiveSP: setP1ActiveSP,
        restedSP: p1RestedSP, setRestedSP: setP1RestedSP,
        fieldCard: p1FieldCard, setFieldCard: setP1FieldCard,
        phaseCard: p1PhaseCard, setPhaseCard: setP1PhaseCard,
        spBonus: p1NextTurnSPBonus, setSpBonus: setP1NextTurnSPBonus,
        spReduction: p1SpReduction, setSpReduction: setP1SpReduction,
        bonusSP: p1BonusSP, setBonusSP: setP1BonusSP,
      };
    }
    return {
      life: p2Life, setLife: setP2Life,
      deck: p2Deck, setDeck: setP2Deck,
      hand: p2Hand, setHand: setP2Hand,
      field: p2Field, setField: setP2Field,
      graveyard: p2Graveyard, setGraveyard: setP2Graveyard,
      activeSP: p2ActiveSP, setActiveSP: setP2ActiveSP,
      restedSP: p2RestedSP, setRestedSP: setP2RestedSP,
      fieldCard: p2FieldCard, setFieldCard: setP2FieldCard,
      phaseCard: p2PhaseCard, setPhaseCard: setP2PhaseCard,
      spBonus: p2NextTurnSPBonus, setSpBonus: setP2NextTurnSPBonus,
      spReduction: p2SpReduction, setSpReduction: setP2SpReduction,
      bonusSP: p2BonusSP, setBonusSP: setP2BonusSP,
    };
  }, [currentPlayer,
      p1Life, p1Deck, p1Hand, p1Field, p1Graveyard, p1ActiveSP, p1RestedSP, p1FieldCard, p1PhaseCard, p1NextTurnSPBonus, p1SpReduction, p1BonusSP,
      p2Life, p2Deck, p2Hand, p2Field, p2Graveyard, p2ActiveSP, p2RestedSP, p2FieldCard, p2PhaseCard, p2NextTurnSPBonus, p2SpReduction, p2BonusSP,
      setP1Life, setP1Deck, setP1Hand, setP1Field, setP1Graveyard, setP1ActiveSP, setP1RestedSP, setP1FieldCard, setP1PhaseCard, setP1NextTurnSPBonus, setP1SpReduction, setP1BonusSP,
      setP2Life, setP2Deck, setP2Hand, setP2Field, setP2Graveyard, setP2ActiveSP, setP2RestedSP, setP2FieldCard, setP2PhaseCard, setP2NextTurnSPBonus, setP2SpReduction, setP2BonusSP]);

  /**
   * フェイズ処理メイン関数
   * @param phaseIndex - フェイズインデックス (0-4)
   */
  const processPhase = useCallback((phaseIndex: number): void => {
    const player = getCurrentPlayerData();

    // トリガー用コンテキスト
    const triggerContext = {
      currentPlayer,
      setP1Life,
      setP2Life,
      setP1Field,
      setP2Field,
      setP1Hand,
      setP2Hand,
      setP1Deck,
      setP2Deck,
      setP1Graveyard,
      setP2Graveyard,
      setP1ActiveSP,
      setP2ActiveSP,
      setP1RestedSP,
      setP2RestedSP,
      p1Field,
      p2Field,
      p1Hand,
      p2Hand,
      p1Deck,
      p2Deck,
      p1Graveyard,
      p2Graveyard,
      p1Life,
      p2Life,
      p1ActiveSP,
      p2ActiveSP,
      p1RestedSP,
      p2RestedSP,
      addLog,
      setPendingHandSelection,
      setPendingGraveyardSelection,
      setShowGraveyardViewer,
      setPendingDeckReview,
      setPendingMonsterTarget,
      // フェイズカード（ON_OPPONENT_DRAW等のトリガーで使用）
      p1PhaseCard,
      p2PhaseCard,
      setP1PhaseCard,
      setP2PhaseCard,
      // 深海領域（フィールドカードトリガー等で使用）
      p1DeepSeaZone,
      p2DeepSeaZone,
      setP1DeepSeaZone,
      setP2DeepSeaZone,
      myDeepSeaZone: currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone,
      setMyDeepSeaZone: currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone,
    };

    switch (phaseIndex) {
      case 0: // ターン開始フェイズ
        // SPトークン追加（最大10）+ ボーナス - 減少
        const totalSP = player.activeSP + player.restedSP;
        const spBonus = player.spBonus || 0;
        const spReduction = player.spReduction || 0;
        // プレイヤー状態異常からのSP増加減少（深淵の呪縛等）
        const myStatusEffectsForSP = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
        const statusSpReduction = statusEffectEngine.getSPGainReduction(myStatusEffectsForSP);
        const totalSpReduction = spReduction + statusSpReduction;
        const netSpGain = Math.max(0, 1 + spBonus - totalSpReduction); // 通常1 + ボーナス - 減少（最小0）
        if (totalSP < MAX_SP && netSpGain > 0) {
          const actualGain = Math.min(netSpGain, MAX_SP - totalSP);
          player.setActiveSP(prev => Math.min(prev + actualGain, MAX_SP));
          if (spBonus > 0 || totalSpReduction > 0) {
            const modifiers = [];
            if (spBonus > 0) modifiers.push(`ボーナス+${spBonus}`);
            if (totalSpReduction > 0) modifiers.push(`減少-${totalSpReduction}`);
            addLog(`プレイヤー${currentPlayer}: SPトークン+${actualGain}（${modifiers.join('、')}）`, totalSpReduction > 0 ? 'damage' : 'heal');
          } else {
            addLog(`プレイヤー${currentPlayer}: SPトークン+1`, 'info');
          }
        } else if (netSpGain === 0) {
          const reason = statusSpReduction > 0 ? '【深淵の呪縛】効果' : '【壮麗】効果';
          addLog(`プレイヤー${currentPlayer}: SP増加なし（${reason}）`, 'damage');
        }
        // SPボーナスとSP減少をリセット
        if (player.spBonus > 0) {
          player.setSpBonus(0);
        }
        if (player.spReduction > 0) {
          player.setSpReduction(0);
        }
        // SP_GAIN_REDUCTION 状態異常を解除（1回限りの効果）
        if (statusSpReduction > 0) {
          const setMyStatusEffectsForSP = currentPlayer === 1 ? setP1StatusEffects : setP2StatusEffects;
          const updatedEffects = statusEffectEngine.removeSPGainReduction(myStatusEffectsForSP);
          setMyStatusEffectsForSP(updatedEffects);
          addLog(`プレイヤー${currentPlayer}の【深淵の呪縛】が解除された`, 'info');
        }
        // レスト状態のSPをアクティブに
        player.setActiveSP(prev => prev + player.restedSP);
        player.setRestedSP(0);

        // モンスターの攻撃可能フラグと技発動フラグをリセット
        player.setField(prev => prev.map(m => m ? { ...m, canAttack: true, usedSkillThisTurn: false } : null));
        setChargeUsedThisTurn(false);

        // フィールドカードの使用済みフラグをリセット（ルミナスの聖域など）
        player.setFieldCard(prev => prev ? { ...prev, usedDestroyPreventionThisTurn: false } : null);

        // 状態異常のターン開始時処理（眠り・凍結の解除判定、寄生ATK減少）
        processStatusEffectsTurnStart({ setP1Field, setP2Field, addLog });

        // プレイヤー状態異常のターン開始処理（バフのduration減少・解除）
        const setMyStatusEffects = currentPlayer === 1 ? setP1StatusEffects : setP2StatusEffects;
        const myStatusEffects = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
        const playerStatusResult = statusEffectEngine.processPlayerTurnStart(myStatusEffects);
        if (playerStatusResult.expiredEffects.length > 0) {
          playerStatusResult.expiredEffects.forEach(effect => {
            const displayName = getStatusDisplayName(effect.type);
            addLog(`プレイヤー${currentPlayer}の「${displayName}」効果が終了`, 'info');
          });
          setMyStatusEffects([...playerStatusResult.updatedEffects]);
        }

        // 檻（CAGED）解除選択チェック：自分のフィールドにCAGEDモンスターがいれば解除選択を表示
        const checkCagedMonsters = () => {
          const myField = currentPlayer === 1 ? p1Field : p2Field;
          for (let i = 0; i < myField.length; i++) {
            const monster = myField[i];
            if (monster && statusEffectEngine.hasStatus(monster, STATUS_EFFECT_TYPES.CAGED)) {
              // SP解除可能なモンスターを発見
              setPendingCageRelease({
                monsterName: monster.name,
                slotIndex: i,
                fieldPlayer: currentPlayer,
                spCost: 1,
              });
              return; // 最初の1体を見つけたら終了（解除後に再チェック）
            }
          }
        };
        checkCagedMonsters();

        // 逆転の足音等によるターン開始時ライフコスト処理
        const processPendingLifeCost = () => {
          const setField = currentPlayer === 1 ? setP1Field : setP2Field;
          const setLife = currentPlayer === 1 ? setP1Life : setP2Life;
          let totalLifeCost = 0;

          setField(prev => prev.map(m => {
            if (m && m.pendingLifeCost) {
              totalLifeCost += m.pendingLifeCost;
              addLog(`${m.name}の代償: ライフ-${m.pendingLifeCost}`, 'damage');
              const { pendingLifeCost, ...rest } = m;
              return rest;
            }
            return m;
          }));

          if (totalLifeCost > 0) {
            setLife(prev => Math.max(0, prev - totalLifeCost));
          }
        };
        processPendingLifeCost();

        // C0000313 アクア・リヴァイアサン上級技: 対象プレイヤーのターン開始時にSP1つをレスト状態にする
        if (pendingOpponentSPRest === currentPlayer) {
          // このプレイヤーが対象なのでSPをレスト状態にする
          if (player.activeSP > 0) {
            player.setActiveSP(prev => Math.max(0, prev - 1));
            player.setRestedSP(prev => prev + 1);
            addLog(`アクア・リヴァイアサンの効果: プレイヤー${currentPlayer}のSP1個がレスト状態に！`, 'damage');
          } else {
            addLog(`アクア・リヴァイアサンの効果: SPがないため効果なし`, 'info');
          }
          // フラグをクリア
          dispatch(gameActions.setGameFlags({ pendingOpponentSPRest: null }));
        }

        // C0000314 聖光フェニックス: 墓地から攻撃力1500で復活
        const processPendingRevival = () => {
          const setField = currentPlayer === 1 ? setP1Field : setP2Field;
          const graveyard = currentPlayer === 1 ? p1Graveyard : p2Graveyard;
          const setGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;
          const field = currentPlayer === 1 ? p1Field : p2Field;

          // 復活待ちカードを探す（このプレイヤーがオーナーの場合のみ）
          const cardToRevive = graveyard.find(card =>
            card.pendingRevival &&
            card.pendingRevival.owner === currentPlayer
          );

          if (cardToRevive && cardToRevive.pendingRevival) {
            // 空きスロットを探す
            const emptySlot = field.findIndex(slot => slot === null);
            const revivalInfo = cardToRevive.pendingRevival;
            if (emptySlot !== -1) {
              // 復活処理
              const revivedAttack = revivalInfo.attack || cardToRevive.attack || 0;
              const revivedHp = cardToRevive.hp || 0;
              const revivedMonster: MonsterInstance = {
                ...cardToRevive,
                attack: revivedAttack,
                currentAttack: revivedAttack,
                hp: revivedHp,
                currentHp: revivedHp,
                maxHp: revivedHp,
                canAttack: !summoningSickness, // 召喚酔い設定に依存
                usedSkillThisTurn: false,
                charges: [],
                statusEffects: [],
                phoenixRevived: true, // 復活済みフラグ
                owner: currentPlayer,
                pendingRevival: undefined, // フラグをクリア
              };

              setField(prev => {
                const newField = [...prev];
                newField[emptySlot] = revivedMonster;
                return newField;
              });

              // 墓地からカードを除去
              setGraveyard(prev => prev.filter(c => c.uniqueId !== cardToRevive.uniqueId));

              addLog(`聖光フェニックスが攻撃力${revivedMonster.currentAttack}で復活！`, 'heal');

              // トリガー登録
              registerCardTriggers(revivedMonster, currentPlayer, emptySlot);
            } else {
              addLog('聖光フェニックスの復活: フィールドに空きがありません', 'info');
              // フラグをクリア（復活できなかったが待機も終了）
              setGraveyard(prev => prev.map(c => {
                if (c.uniqueId === cardToRevive.uniqueId) {
                  const { pendingRevival, ...rest } = c;
                  return { ...rest, phoenixRevived: true };
                }
                return c;
              }));
            }
          }
        };
        processPendingRevival();

        // 後攻ボーナスSP付与（後攻プレイヤーの初回ターンのみ）
        if (secondPlayerBonusSP && isFirstTurn && currentPlayer !== firstPlayer) {
          player.setBonusSP(1);
          addLog(`☆後攻ボーナス: プレイヤー${currentPlayer}にボーナスSP+1！`, 'heal');
        }

        // 後攻ボーナス終了：後攻の3ターン目開始時にSPを1減らす（以降は先攻と同じペースに）
        if (turn === 3 && currentPlayer !== firstPlayer) {
          player.setActiveSP(prev => Math.max(0, prev - 1));
          addLog(`後攻ボーナス終了: SPが1減少`, 'info');
        }

        // ターン開始時トリガーを発火
        fireTrigger(TRIGGER_TYPES.ON_TURN_START_SELF, triggerContext);

        // Phase D-1: dispatchのみで状態更新
        dispatch(gameActions.setPhase(1));
        break;

      case 1: // ドローフェイズ
        if (player.deck.length > 0) {
          const drawnCard = player.deck[0];
          player.setDeck(prev => prev.slice(1));
          player.setHand(prev => [...prev, drawnCard]);
          addLog(`プレイヤー${currentPlayer}: 1枚ドロー`, 'info');
        } else {
          addLog(`プレイヤー${currentPlayer}: デッキ切れ！`, 'damage');
        }

        // ドローフェイズトリガーを発火
        fireTrigger(TRIGGER_TYPES.ON_DRAW_PHASE_SELF, triggerContext);

        // 相手のON_OPPONENT_DRAWトリガーを発火（虚蝕の呪詛等のフェイズカード効果）
        {
          const opponentPlayer: PlayerNumber = currentPlayer === 1 ? 2 : 1;
          const opponentDrawTriggerContext = {
            ...triggerContext,
            currentPlayer: opponentPlayer, // トリガー発動者は相手視点
          };
          fireTrigger(TRIGGER_TYPES.ON_OPPONENT_DRAW, opponentDrawTriggerContext);
        }

        // Phase D-1: dispatchのみで状態更新
        dispatch(gameActions.setPhase(2));
        break;

      case 2: // メインフェイズ
        // プレイヤーの操作待ち（自動進行なし）
        // ON_MAIN_PHASE_SELFは任意発動（OPTIONAL）なので自動発火しない
        // 技と同様にカード選択時にUIに表示され、プレイヤーが手動で発動する
        break;

      case 3: // バトルフェイズ
        // 先攻1ターン目はnextPhase()でスキップされるが、フォールバックとしてチェック
        if (isFirstTurn && currentPlayer === firstPlayer) {
          // Phase D-1: dispatchのみで状態更新
          dispatch(gameActions.setPhase(4));
          return; // トリガー発火せずに終了
        }

        // バトルフェイズ開始時トリガーを発火（両方発火：汎用 + 自分専用）
        fireTrigger(TRIGGER_TYPES.ON_BATTLE_PHASE_START, triggerContext);
        fireTrigger(TRIGGER_TYPES.ON_BATTLE_PHASE_START_SELF, triggerContext);
        break;

      case 4: // エンドフェイズ
        // エンドフェイズトリガーを発火
        fireTrigger(TRIGGER_TYPES.ON_END_PHASE_SELF, triggerContext);
        fireTrigger(TRIGGER_TYPES.ON_END_PHASE, triggerContext);
        // 相手視点のエンドフェイズトリガーを発火（相手のON_OPPONENT_END_PHASE）
        fireTrigger(TRIGGER_TYPES.ON_OPPONENT_END_PHASE, triggerContext);

        // 墓地カードのエンドフェイズトリガーを発火
        {
          const graveyard = currentPlayer === 1 ? p1Graveyard : p2Graveyard;
          graveyard.forEach((card) => {
            const graveyardTriggers = getCardGraveyardTriggers(card, triggerContext);
            graveyardTriggers.forEach((trigger) => {
              // ParsedTrigger has 'type', ActivatableTrigger has 'triggerType'
              const triggerWithType = trigger as typeof trigger & { type?: string };
              if ((triggerWithType.type === TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD ||
                   trigger.triggerType === TRIGGER_TYPES.ON_END_PHASE_FROM_GRAVEYARD) &&
                  trigger.activationType === 'automatic' &&
                  trigger.canActivate) {
                try {
                  if (typeof trigger.effect === 'function') {
                    trigger.effect({ ...triggerContext, card });
                  }
                } catch (error) {
                  console.error(`墓地トリガー発動エラー: ${card.name}`, error);
                }
              }
            });
          });
        }

        // 【魂結】リンクダメージ処理（自分のターン終了時に相手にダメージ）
        {
          const myField = currentPlayer === 1 ? p1Field : p2Field;
          const setOpponentLife = currentPlayer === 1 ? setP2Life : setP1Life;
          // addLog の型をprocessLinkEndPhaseDamage互換にキャスト
          processLinkEndPhaseDamage(myField, setOpponentLife, addLog as (message: string, type?: string) => void);
        }

        // 状態異常のエンドフェイズ処理（深蝕による攻撃力減少、持続時間減少）
        // 両プレイヤーのモンスターを処理（expiresAfterEndPhasesのカウントダウンのため）

        // C0000567「魂喰らいの魔女・マリス」のチェック
        // マリスが場にいる場合、相手の彷徨い状態は解除されない
        const p1HasMaris = p1Field.some(m => m?.id === 'C0000567');
        const p2HasMaris = p2Field.some(m => m?.id === 'C0000567');

        const processFieldEndPhase = (
          setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>,
          fieldOwner: PlayerNumber,
          opponentHasMaris: boolean
        ): void => {
          setField(prev => prev.map((m): MonsterInstance | null => {
            if (!m) return null;
            // 相手にマリスがいる場合、彷徨いのdurationをスキップ
            const skipTypes = opponentHasMaris ? [STATUS_EFFECT_TYPES.WANDERING as StatusEffectTypeValue] : [];
            const result = statusEffectEngine.processEndPhase(m, { skipDurationDecrement: skipTypes });
            // 深蝕による攻撃力減少ログ（モンスターオーナーのエンドフェイズでのみ表示）
            if (result.atkReduction > 0 && fieldOwner === currentPlayer) {
              addLog(`${m.name}は深蝕により攻撃力が${result.atkReduction}減少！`, 'damage');
            }
            // 持続時間終了で解除されたエフェクトのログ
            if (result.removedEffects.length > 0) {
              result.removedEffects.forEach(effect => {
                addLog(`${m.name}の${getStatusDisplayName(effect.type)}が解除された！`, 'info');
              });
            }
            // MonsterWithStatus -> MonsterInstance キャスト（uniqueIdは既にmから引き継いでいる）
            return result.monster as MonsterInstance;
          }));
        };
        // 両プレイヤーのフィールドを処理（相手にマリスがいるかをチェック）
        processFieldEndPhase(setP1Field, 1, p2HasMaris);
        processFieldEndPhase(setP2Field, 2, p1HasMaris);

        // プレイヤー状態異常のエンドフェイズ処理（毒ダメージ等）
        const playerStatusEffects = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
        const poisonResult = statusEffectEngine.processPlayerEndPhase(playerStatusEffects);
        if (poisonResult.damage > 0) {
          player.setLife(prev => Math.max(0, prev - poisonResult.damage));
          addLog(`プレイヤー${currentPlayer}は毒により${poisonResult.damage}ダメージ！`, 'damage');
        }
        // プレイヤー状態異常の更新（持続時間減少）
        if (currentPlayer === 1) {
          setP1StatusEffects(poisonResult.effects);
        } else {
          setP2StatusEffects(poisonResult.effects);
        }

        // 深海領域の深度増加処理（自分のエンドフェイズ時）
        {
          const myDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
          const setMyDeepSeaZone = currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;
          const myField = currentPlayer === 1 ? p1Field : p2Field;

          // 深海領域が開放されていてカードがある場合のみ処理
          if (myDeepSeaZone.isUnlocked && myDeepSeaZone.cards.length > 0) {
            // 【暗黒水域】を持つモンスターがフィールドにいる場合、追加深度+1
            const hasAnkokuSuiiki = myField.some(m => m && hasAnkokuSuiikiKeyword(m as CardWithId));
            const bonusDepth = hasAnkokuSuiiki ? 1 : 0;

            const result = incrementDepth(myDeepSeaZone, bonusDepth);

            if (result.incrementedCount > 0) {
              setMyDeepSeaZone(result.updatedZone);
              if (bonusDepth > 0) {
                addLog(`深海領域: ${result.incrementedCount}体の深度が2増加（【暗黒水域】効果）`, 'info');
              } else {
                addLog(`深海領域: ${result.incrementedCount}体の深度が1増加`, 'info');
              }
            }
          }
        }

        // ターン終了時に使用済みフラグをリセット
        resetTurnFlags();
        continuousEffectEngine.resetTurnFlags();

        // 魔法カード使用制限の解除（触覚持ち粘液獣の効果は相手エンドフェイズで終了）
        if (currentPlayer === 1 && p1MagicBlocked) {
          setP1MagicBlocked(false);
          addLog('プレイヤー1: 魔法カード使用制限が解除された', 'info');
        } else if (currentPlayer === 2 && p2MagicBlocked) {
          setP2MagicBlocked(false);
          addLog('プレイヤー2: 魔法カード使用制限が解除された', 'info');
        }

        // ダイレクトアタック禁止の解除（ダーク・リヴァイアサン、ブラック・オラクル等）
        {
          const myStatusEffectsForDirect = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
          const setMyStatusEffectsForDirect = currentPlayer === 1 ? setP1StatusEffects : setP2StatusEffects;
          if (!statusEffectEngine.canDirectAttack(myStatusEffectsForDirect)) {
            const updatedEffects = statusEffectEngine.removeNoDirectAttack(myStatusEffectsForDirect);
            setMyStatusEffectsForDirect(updatedEffects);
            addLog(`プレイヤー${currentPlayer}のダイレクトアタック禁止が解除された`, 'info');
          }
        }

        // 寄生効果の無効化解除（相手エンドフェイズで効果無効化が終了）
        processStatusEffectsEndPhase({ setP1Field, setP2Field, addLog }, currentPlayer);

        // 棘根の罠等によるターン終了時ダメージ処理
        const processThornTrapDamage = () => {
          // 相手フィールドにthornTrapDamageフラグがあるモンスターを探す
          const opponentField = currentPlayer === 1 ? p2Field : p1Field;
          const setOpponentField = currentPlayer === 1 ? setP2Field : setP1Field;
          const setOpponentLife = currentPlayer === 1 ? setP2Life : setP1Life;
          let totalDamage = 0;

          // 対象モンスターを探してダメージを計算
          opponentField.forEach(m => {
            if (m && m.thornTrapDamage) {
              totalDamage += m.thornTrapDamage;
              addLog(`棘根の罠の効果: ${m.thornTrapDamage}ダメージ！`, 'damage');
            }
          });

          // ダメージを与える
          if (totalDamage > 0) {
            setOpponentLife(prev => Math.max(0, prev - totalDamage));
          }

          // フラグをクリア
          setOpponentField(prev => prev.map(m => {
            if (m && m.thornTrapDamage) {
              const { thornTrapDamage, ...rest } = m;
              return rest;
            }
            return m;
          }));
        };
        processThornTrapDamage();

        // ターン終了時までの効果をリセット（攻撃力バフ、破壊耐性等）
        const clearEndOfTurnEffects = (
          setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>
        ): void => {
          setField(prev => prev.map(m => {
            if (!m) return m;
            let updated = m;

            updated = clearTemporaryAttack(m);
            if (updated !== m) {
              addLog(`${m.name}の一時的な攻撃力補正が終了（${updated.currentAttack}）`, 'info');
            }

            // 破壊耐性をリセット
            if (m.indestructibleUntilEndOfTurn) {
              addLog(`${m.name}の破壊耐性が終了`, 'info');
              const { indestructibleUntilEndOfTurn, ...rest } = updated;
              updated = rest;
            }

            // 反撃無効フラグをリセット（呪影の牙狼ウルフェイン等）
            if (m.noCounterDamage) {
              const { noCounterDamage, ...rest } = updated;
              updated = rest;
            }

            // 反撃ダメージ率をリセット（棘の剛毛猪イノブリスト等）
            if (m.counterDamageRate !== undefined) {
              const { counterDamageRate, ...rest } = updated;
              updated = rest;
            }

            return updated;
          }));
        };
        // 「ターン終了時まで」は所有者を問わずこのエンドフェイズで解除する。
        clearEndOfTurnEffects(setP1Field);
        clearEndOfTurnEffects(setP2Field);

        // ターン終了時に破壊されるモンスターを処理（荒海の強襲、コントロール奪取等）
        const processDestroyAtEndOfTurn = (
          field: (MonsterInstance | null)[],
          setField: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>,
          setGraveyard: React.Dispatch<React.SetStateAction<CardWithId[]>>,
          setLife: React.Dispatch<React.SetStateAction<number>>
        ): void => {
          const toDestroy = field.filter((m): m is MonsterInstance => m !== null && m.destroyAtEndOfTurn === true);
          if (toDestroy.length === 0) return;

          // damageOwnerOnDestroyによるダメージ計算
          let totalDamage = 0;
          toDestroy.forEach(m => {
            if (m.damageOwnerOnDestroy && m.damageOwnerOnDestroy > 0) {
              totalDamage += m.damageOwnerOnDestroy;
              addLog(`${m.name}の破壊により${m.damageOwnerOnDestroy}ダメージ！`, 'damage');
            }
          });

          // フィールドから除去してnullに置換
          setField(prev => prev.map(m => {
            if (m && m.destroyAtEndOfTurn) {
              addLog(`${m.name}はターン終了時に破壊された！`, 'damage');
              return null;
            }
            return m;
          }));

          // 墓地に送る
          setGraveyard(prev => [
            ...prev,
            ...toDestroy.map(m => {
              // destroyAtEndOfTurn, damageOwnerOnDestroyフラグを除去して墓地に送る
              const { destroyAtEndOfTurn, damageOwnerOnDestroy, ...cardData } = m;
              return cardData as CardWithId;
            })
          ]);

          // ダメージを適用
          if (totalDamage > 0) {
            setLife(prev => Math.max(0, prev - totalDamage));
          }
        };

        // 現在のプレイヤーのフィールドを処理
        if (currentPlayer === 1) {
          processDestroyAtEndOfTurn(p1Field, setP1Field, setP1Graveyard, setP1Life);
        } else {
          processDestroyAtEndOfTurn(p2Field, setP2Field, setP2Graveyard, setP2Life);
        }

        // エンドフェイズまでの一時的コスト軽減をクリア（水晶のマーメイド等）
        const clearTempCostModifier = (hand: HandCard[]): HandCard[] => hand.map(c => {
          if (c.tempCostModifierUntilEndPhase) {
            const { tempCostModifier, tempCostModifierSource, tempCostModifierUntilEndPhase, ...rest } = c;
            return rest;
          }
          return c;
        });
        setP1Hand(prev => clearTempCostModifier(prev));
        setP2Hand(prev => clearTempCostModifier(prev));

        // Phase D-1: dispatchのみでターン終了（phase, currentPlayer, turn, isFirstTurnを更新）
        dispatch(gameActions.endTurn());
        addLog(`プレイヤー${currentPlayer}のターン終了`, 'info');
        break;

      default:
        break;
    }
  }, [getCurrentPlayerData, currentPlayer, isFirstTurn, firstPlayer, turn, pendingOpponentSPRest, summoningSickness, secondPlayerBonusSP,
      p1Field, p2Field, p1Hand, p2Hand, p1Deck, p2Deck,
      p1Graveyard, p2Graveyard, p1Life, p2Life, p1StatusEffects, p2StatusEffects,
      p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
      p1PhaseCard, p2PhaseCard, p1MagicBlocked, p2MagicBlocked,
      p1DeepSeaZone, p2DeepSeaZone,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
      setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
      setP1PhaseCard, setP2PhaseCard, setP1StatusEffects, setP2StatusEffects,
      setP1MagicBlocked, setP2MagicBlocked,
      setP1DeepSeaZone, setP2DeepSeaZone,
      setChargeUsedThisTurn, setPendingCageRelease,
      setPendingHandSelection, setPendingGraveyardSelection, setShowGraveyardViewer,
      setPendingDeckReview, setPendingMonsterTarget,
      addLog, dispatch]);

  return {
    processPhase,
  };
};

export default usePhaseProcessor;
